import TableScrollShell from "../../common/TableScrollShell";
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ChangeRuleModal from "./ChangeRuleModal";
import NewRuleModal from "./NewRuleModal";
import { toast } from "../../../utils/toast";
import AppContext from "../../../context/AppContext";
import CommonButton from "../../common/CommonButton";
import { assignAll, DeleteSpecialAssignmentRule, getAllSpecialAssign } from "../../../service/Configuration";
import { CheckCheck, Search, X, Plus, Map, Pencil, Trash2, Inbox, SearchX, Zap } from "lucide-react";
import UpdateRuleModal from "./UpdateRuleModal";
import UrgentAssignmentModal from "./UrgentAssignmentModal";
import translations from "../../common/Translations";
import DeletePermission from "../../suspence/DeletePermission";
import PageSizeSelector from "../../common/PageSizeSelector";
import { Spinner } from "../../../Spinner";
import ActivityMapFilter from "./ActivityMapFilter";
import { canShowActionColumn, getPermissionByScreenCode } from "../../../utils/permissions";
import ActionIconButton from "../../common/ActionIconButton";
import PagerArrowButton from "../../common/PagerArrowButton";
import ProjectAutoAssignToggle from "../ProjectAutoAssignToggle";
import SearchFieldPicker from "../../common/SearchFieldPicker";
import ReactSelector from "../../common/CommonReactSelector";
import { getActivityListByProjectId } from "../../../service/User";
import { getPermittedActivities } from "../../../utils/activityPermissions";

// Same field-scoped search pattern as UserToolbar.js (User Management) and
// TaskListToolbar.js: a joined field-picker + search box via the shared
// SearchFieldPicker component, instead of one box silently matching every
// field at once.
const SEARCH_SCOPES = [
    { value: "assignTo", labelKey: "assignTo", fallback: "Assign To" },
    { value: "workflow", labelKey: "sarWorkflow", fallback: "Workflow" },
    { value: "status", labelKey: "status", fallback: "Status" },
    // Uses the short "Geo Code" label (geoCodeLabel), not the `geoCodes`
    // key -- that one is "Selected Geo Codes", the table column header/
    // popover title, which is too long for the picker button and got
    // truncated to "Selected Geo ...".
    { value: "geoCodes", labelKey: "geoCodeLabel", fallback: "Geo Code" },
];

// Deterministic per-name color + "Assign To" avatar cell, and the
// overflow cell for Geo Codes/Type Codes/Amount Range -- same local,
// file-owned helper pattern UserList.js uses for its own table cells
// (nameToHslColor/initials/MultiValueCell etc. all live in that file too,
// not in a shared component module).
const AVATAR_PALETTE = [
    "bg-indigo-500", "bg-violet-500", "bg-rose-500", "bg-amber-500",
    "bg-teal-500", "bg-blue-500", "bg-fuchsia-500", "bg-cyan-600",
];
function avatarColor(name) {
    const value = name || "";
    let sum = 0;
    for (let i = 0; i < value.length; i++) sum += value.charCodeAt(i);
    return AVATAR_PALETTE[sum % AVATAR_PALETTE.length];
}
function initial(name) {
    return (name || "?").trim().charAt(0).toUpperCase();
}
function AssignToCell({ name, scopeLabel }) {
    if (!name) return <span className="text-slate-400">—</span>;
    return (
        <div className="flex items-center gap-3">
            <div className={`h-8 w-8 rounded-full ${avatarColor(name)} text-white text-xs font-semibold flex items-center justify-center shrink-0`}>
                {initial(name)}
            </div>
            <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate max-w-[160px]">{name}</p>
                <p className="text-xs text-slate-500 truncate">{scopeLabel}</p>
            </div>
        </div>
    );
}

const HOVER_TOOLTIP_EDGE_MARGIN = 10;
const HOVER_TOOLTIP_GAP = 8;

// Same portal + fixed-position + flip-above-when-clipped-below technique as
// UserList.js's HoverListPopover (used by its Department/Project/Group "+N"
// popovers) -- portal-rendered to <body> and measured after mount so it can
// flip to render above the trigger instead of being clipped by the table's
// scrolling container when there isn't room below (and vice versa near the
// top of the table).
function HoverListPopover({ isOpen, anchorRef, children }) {
    const tooltipRef = useRef(null);
    const [style, setStyle] = useState({ position: "fixed", visibility: "hidden" });

    useLayoutEffect(() => {
        if (!isOpen) return;
        const updatePosition = () => {
            if (!anchorRef?.current || !tooltipRef.current) return;
            const triggerRect = anchorRef.current.getBoundingClientRect();
            const tipRect = tooltipRef.current.getBoundingClientRect();

            const fitsBelow = triggerRect.bottom + HOVER_TOOLTIP_GAP + tipRect.height + HOVER_TOOLTIP_EDGE_MARGIN <= window.innerHeight;
            const top = fitsBelow
                ? triggerRect.bottom + HOVER_TOOLTIP_GAP
                : Math.max(HOVER_TOOLTIP_EDGE_MARGIN, triggerRect.top - tipRect.height - HOVER_TOOLTIP_GAP);

            let left = triggerRect.left;
            const maxLeft = window.innerWidth - tipRect.width - HOVER_TOOLTIP_EDGE_MARGIN;
            left = Math.min(Math.max(left, HOVER_TOOLTIP_EDGE_MARGIN), Math.max(maxLeft, HOVER_TOOLTIP_EDGE_MARGIN));

            setStyle({ position: "fixed", top, left, visibility: "visible" });
        };
        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);
        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [isOpen, anchorRef]);

    if (!isOpen) return null;

    return createPortal(
        <div ref={tooltipRef} style={style} className="z-[60] w-56 bg-white rounded-lg border border-slate-200 shadow-lg p-2">
            {children}
        </div>,
        document.body
    );
}

// Keeps a hover popover open while the cursor crosses the small visual gap
// between the trigger and the popover itself (same as UserList.js). Without
// the delay, the gap sits outside the trigger's box, so the browser fires
// mouseleave -- and the popover unmounts -- before the cursor ever reaches
// it, making the list impossible to scroll into.
function useDelayedHover(delay = 150) {
    const [hover, setHover] = useState(false);
    const closeTimerRef = useRef(null);

    useEffect(() => () => {
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    }, []);

    const onMouseEnter = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setHover(true);
    };

    const onMouseLeave = () => {
        closeTimerRef.current = setTimeout(() => setHover(false), delay);
    };

    return { hover, onMouseEnter, onMouseLeave };
}

// Mirrors UserList.js's MultiValueCell exactly: 2 or fewer values render as
// plain truncated comma-text; beyond that, a rounded-full "+N" badge reveals
// the full list as a row-per-item hover popover. `label` is optional and
// only used as the popover's header (e.g. "Selected Geo Codes (6)").
function ChipOverflow({ items, label }) {
    const { hover, onMouseEnter, onMouseLeave } = useDelayedHover();
    const triggerRef = useRef(null);
    if (!items || items.length === 0) return <span className="text-slate-300">—</span>;

    const shown = items.slice(0, 2);
    const extra = items.length - shown.length;

    if (extra <= 0) {
        return <span className="text-xs text-slate-500 truncate max-w-full inline-block align-middle">{items.join(", ")}</span>;
    }

    return (
        <div
            ref={triggerRef}
            className="relative inline-flex items-center gap-1.5 max-w-full"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <span className="text-xs text-slate-500 truncate max-w-[120px] inline-block align-middle">{shown.join(", ")}</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-semibold shrink-0 bg-slate-100 text-slate-500">
                +{extra}
            </span>
            <HoverListPopover isOpen={hover} anchorRef={triggerRef}>
                {label && (
                    <div className="text-[11px] font-semibold px-1.5 pb-1.5 text-slate-400 uppercase tracking-wide">
                        {label} ({items.length})
                    </div>
                )}
                <div className="space-y-1 max-h-48 overflow-y-auto overflow-x-hidden custom-scrollbar">
                    {items.map((it, i) => (
                        <div key={`${it}-${i}`} className="px-1.5 py-1 rounded-md text-sm text-slate-700 break-words">
                            {it}
                        </div>
                    ))}
                </div>
            </HoverListPopover>
        </div>
    );
}

// search/searchScope are lifted to ConfigurationMain.js (which stays mounted
// while switching tabs) so the typed term and selected field survive a tab
// switch instead of resetting -- this component only owned local state
// before, which meant unmounting on tab-away threw it away. Clearing (the X
// button) and switching the search field still reset the value the same way
// as before, just through the lifted setter now.
const AssignmentTable = ({ tabBar, search, onSearchChange: setSearch, searchScope, onSearchScopeChange: setSearchScope }) => {
    const [open, setOpen] = useState(false);
    const [updateRule, setUpdateRule] = useState(null);
    const [updateOpen, setUpdateOpen] = useState(false);
    const [newRule, setNewRule] = useState(false);
    const [urgentOpen, setUrgentOpen] = useState(false);
    const [deleteRule, setDeleteRule] = useState(null);
    const [specialAssign, setSpecialAssign] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const { langMode, projectId, theme } = useContext(AppContext);
    const isRtl = langMode === "ar" || langMode === "ur";
    const showAssignActionColumn = canShowActionColumn("SPECIAL_ASSIGNMENT_RULES");
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false)
    const [userId, setUserId] = useState()
    // Workflow filter for get-all/assign-all. null = all workflows (no
    // workflowId param). reloadKey forces a refetch after a modal saves even
    // when the page/size/filter deps haven't changed.
    const [workflowOptions, setWorkflowOptions] = useState([]);
    const [workflowFilter, setWorkflowFilter] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const customVarPerm = getPermissionByScreenCode("SPECIAL_ASSIGNMENT_RULES");
    const canUrgentAssign = Boolean(customVarPerm?.canUpdate);

    const totalPages = Math.ceil(totalItems / pageSize);
    const displayStart = totalItems > 0 ? currentPage * pageSize + 1 : 0;
    const displayEnd = totalItems > 0 ? Math.min((currentPage + 1) * pageSize, totalItems) : 0;
    const goToPreviousPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const fetchAllSpecialAssign = async () => {
        setLoading(true);
        try {
            const data = await getAllSpecialAssign(projectId, currentPage, pageSize, workflowFilter?.value);
            setSpecialAssign(data?.ruleList || []);
            setTotalItems(data?.totalCount || 0);
        } catch (error) {
            console.error("Error fetching special assignments:", error);
            toast.error(translations[langMode].failtoloadasign);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignAll = async () => {
        try {
            const data = await assignAll(projectId, workflowFilter?.value);
            if (data) {
                toast.success(translations[langMode].unAssignTaskSuccessMeg);
                fetchAllSpecialAssign();
            } else {
                toast.error(data?.body?.value || translations[langMode].failedtoassign);
            }
        } catch (error) {
            console.error("Error assigning all rules:", error);
        }
    };

    const handleClick = () => {
        setNewRule(true);
        toast.info(translations[langMode].OnClickTheOldAssignmentRuleDisabled);
    };

    const handleDeleteSpecialAssignRule = async () => {
        try {
            if (!deleteRule?.id) {
                toast.error(translations[langMode].invalideruleid);
                return;
            }
            const response = await DeleteSpecialAssignmentRule(deleteRule?.id);
            if (response?.header?.code === 600) {
                fetchAllSpecialAssign();
                toast.success(translations[langMode].ruledeleted);
            } else {
                toast.error(response?.body?.value || translations[langMode].failedelrule);
            }
        } catch (error) {
            console.error("Error in handleDeleteSpecialAssignRule:", error);
            toast.error(error.message);
            return;
        }
        setDeleteRule(null);
    };

    useEffect(() => {
        if (!projectId) {
            setSpecialAssign([]);
            setTotalItems(0);
            return;
        }
        fetchAllSpecialAssign();
    }, [projectId, currentPage, pageSize, workflowFilter, reloadKey]);

    // Same source CompletionRules.js uses for its workflow dropdown, narrowed
    // to the workflows this user is permitted to see.
    useEffect(() => {
        setWorkflowFilter(null);
        if (!projectId) {
            setWorkflowOptions([]);
            return;
        }
        let cancelled = false;
        getActivityListByProjectId(projectId)
            .then((data) => {
                if (cancelled) return;
                setWorkflowOptions(
                    getPermittedActivities(Array.isArray(data) ? data : [])
                        .filter(Boolean)
                        .map((w) => ({ value: w.workflowId, label: w.workflowName }))
                );
            })
            .catch((error) => {
                console.error("Error fetching workflows:", error);
                if (!cancelled) setWorkflowOptions([]);
            });
        return () => {
            cancelled = true;
        };
    }, [projectId]);

    const handlePageSizeChange = (value) => {
        const size = parseInt(value, 10);
        setPageSize(size);
        setCurrentPage(0);
    };

    const handleWorkflowFilterChange = (option) => {
        setWorkflowFilter(option?.value ? option : null);
        setCurrentPage(0);
    };

    // Called by New/Update rule modals after a successful save -- jumps back
    // to the first page (results are newest first) and refetches with the
    // current workflow filter.
    const handleRulesSaved = () => {
        setCurrentPage(0);
        setReloadKey((k) => k + 1);
    };

    // Legacy rules created before rules were workflow-scoped come back with
    // workflowId: null and apply to every workflow.
    const workflowLabel = (item) =>
        item.workflowId == null
            ? translations[langMode].allWorkflows || "All workflows"
            : item.workflowName || workflowOptions.find((o) => o.value === item.workflowId)?.label || `#${item.workflowId}`;

    // Geo Codes / Type Codes / Amount Range cell -- "All" stays plain text
    // (same allField flag as before), otherwise ChipOverflow (matches
    // UserList.js's MultiValueCell: plain text up to 2 values, then a
    // pill "+N" revealing the full list on hover). Same item[field]/
    // item[allField] data as before; `label` only feeds the hover header.
    const renderChipCell = (item, field, allField, label) => {
        if (item[allField]) {
            return <span className="text-xs text-slate-500">{translations[langMode].All}</span>;
        }
        return <ChipOverflow items={item[field]} label={label} />;
    };

    // Small "User"/"Group" badge label for the Assign To column -- same trick
    // already used on the Assignment Rules table (trims the " Assign" suffix
    // off the existing userAssign/groupAssign translation strings).
    const scopeBadgeLabel = (scope) =>
        scope === "groups"
            ? (translations[langMode]?.groupAssign?.replace(/\s*Assign$/i, "") || "Group")
            : (translations[langMode]?.userAssign?.replace(/\s*Assign$/i, "") || "User");

    // Client-side filter over the currently loaded page -- same pattern as
    // CompletionRules.js/AssignmentRules.js (no new API params, paginati   on
    // untouched). None of the row handlers below rely on array index (they
    // all close over the `item` object directly), so no index bookkeeping
    // is needed here.
    //
    // Field-scoped search -- same architecture as UserList.js's
    // searchTextByScope/matchesScope: each scope resolves to only the
    // field it should match against. Status is compared with startsWith,
    // not includes -- "Inactive" contains "active" as a literal substring
    // (in-ACTIVE-), so an includes() check on the label made typing "active"
    // also match Inactive rows. startsWith keeps "active"/"activ" matching
    // only Active while "in"/"inactive" still matches only Inactive. The
    // label itself is still the *translated* one shown in the table
    // (falling back to English when a language has no translation, same
    // fallback the Status cell uses) so search keeps working in Arabic/Urdu.
    const displayedSpecialAssign = useMemo(() => {
        if (!search.trim()) return specialAssign;
        const q = search.trim().toLowerCase();
        const allLabel = (translations[langMode].All || "All").toLowerCase();
        const activeLabel = (translations[langMode].active || "Active").toLowerCase();
        const inactiveLabel = (translations[langMode].inactive || "Inactive").toLowerCase();

        const matchesAssignTo = (item) => (item.groupName || item.userName || "").toLowerCase().includes(q);
        const matchesStatus = (item) => (item.active === true ? activeLabel : inactiveLabel).startsWith(q);
        const matchesGeoCodes = (item) => {
            if (item.allAreaCodes) return allLabel.includes(q);
            return Array.isArray(item.areaCodes) && item.areaCodes.some((code) => String(code).toLowerCase().includes(q));
        };

        const matchesWorkflow = (item) => workflowLabel(item).toLowerCase().includes(q);

        return specialAssign.filter((item) => {
            if (searchScope === "workflow") return matchesWorkflow(item);
            if (searchScope === "status") return matchesStatus(item);
            if (searchScope === "geoCodes") return matchesGeoCodes(item);
            return matchesAssignTo(item);
        });
    }, [specialAssign, search, searchScope, langMode, workflowOptions]);

    const scopeOptions = SEARCH_SCOPES.map((s) => ({
        value: s.value,
        label: translations[langMode][s.labelKey] || s.fallback,
    }));
    const currentScopeLabel = scopeOptions.find((s) => s.value === searchScope)?.label;
    // Changing the search field makes the previously typed term ambiguous (it
    // was typed against a different field) -- same reset UserToolbar.js does
    // in its own handleScopeSelect.
    const handleScopeSelect = (value) => {
        setSearchScope(value);
        setSearch("");
    };
    const currentSearchPlaceholder =
        searchScope === "assignTo"
            ? translations[langMode].searchByAssignTo
            : `${translations[langMode]?.search || "Search"} ${currentScopeLabel || ""}…`;

    return (
        <div className="flex h-full min-h-0 w-full flex-col">
            <div className="shrink-0 flex flex-col gap-3 mb-3 rounded-xl border border-slate-200 bg-white shadow-sm">
                {tabBar && (
                    <div className="flex w-full min-w-0 items-center justify-between gap-2 px-3 pt-3 sm:px-5 sm:pt-4">
                        {tabBar}
                    </div>
                )}
                <div className="flex flex-col gap-3 px-3 py-3 sm:px-5 sm:py-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h2 className="text-base font-bold text-slate-800 leading-tight sm:text-lg">
                            {translations[langMode].splaignrule}
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {translations[langMode].specialAssignmentRuleSubheading}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        {/* Workflow filter -- scopes both the list (get-all) and
                            Assign All to one workflow; "All workflows" sends no
                            workflowId. */}
                        <div className="w-full sm:w-52" data-testid="special-assignment-workflow-filter">
                            <ReactSelector
                                options={[
                                    { value: null, label: translations[langMode].allWorkflows || "All workflows" },
                                    ...workflowOptions,
                                ]}
                                value={workflowFilter || { value: null, label: translations[langMode].allWorkflows || "All workflows" }}
                                onChange={handleWorkflowFilterChange}
                                isSearchable
                                height="40px"
                                fontSize="14px"
                                menuPortalTarget={document.body}
                                placeholder={translations[langMode].selectWorkflow}
                            />
                        </div>
                        {/* Joined button+input pill -- same visual/interaction pattern as
                            UserToolbar.js (User Management) and TaskListToolbar.js via the
                            shared SearchFieldPicker component, so this table's search looks
                            and behaves like the rest of the app's field-scoped list searches
                            instead of one box silently matching every field at once. */}
                        <div className="flex items-center w-full sm:w-72">
                            <SearchFieldPicker
                                options={scopeOptions}
                                activeValue={searchScope}
                                onSelect={handleScopeSelect}
                                searchPlaceholder={translations[langMode]?.findAField ?? "Find a field..."}
                                isRtl={isRtl}
                                buttonTestId="special-assignment-search-scope-button"
                                buttonClassName="!h-10"
                            />
                            <div className="relative flex-1 flex items-center">
                                <Search size={14} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 shrink-0 ${isRtl ? "right-3" : "left-3"}`} />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={typeof window !== "undefined" && window.innerWidth < 450 ? `${translations[langMode]?.search || "Search"}...` : currentSearchPlaceholder}
                                    title={currentSearchPlaceholder}
                                    aria-label={currentSearchPlaceholder}
                                    className={`w-full h-10 text-sm outline-none bg-white border border-slate-200 focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] placeholder:text-slate-400 ${isRtl ? "pr-9 pl-8 border-r-0 rounded-l-lg" : "pl-9 pr-8 border-l-0 rounded-r-lg"}`}
                                />
                                {search && (
                                    <button
                                        type="button"
                                        onClick={() => setSearch("")}
                                        className={`absolute top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 shrink-0 ${isRtl ? "left-2" : "right-2"}`}
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="grid w-full grid-cols-2 gap-2 sm:contents">
                            <CommonButton colorIntent="success" onClick={handleAssignAll} variant="success" tooltipText={translations[langMode].assignallactiverules} tooltipPosition="top" wrapperClassName="w-full sm:w-auto" className="w-full sm:w-auto !h-10 !min-h-[40px] !rounded-[10px] !px-3 !py-2 !text-xs sm:!px-4 sm:!text-sm shrink-0">
                                <CheckCheck size={18} /> {translations[langMode].assignAll}
                            </CommonButton>
                            <ProjectAutoAssignToggle
                                disabled={!customVarPerm?.canUpdate}
                                wrapperClassName="w-full sm:w-auto"
                                className="w-full sm:w-auto justify-center"
                            />
                            {/* Urgent Task Assignment is only for roles with Update access
                                on the Special Assignment Rules screen (super admin and
                                fullAccess roles pass via getPermissionByScreenCode). */}
                            {canUrgentAssign && (
                                <CommonButton colorIntent="primary" onClick={() => setUrgentOpen(true)} variant="primary" tooltipText={translations[langMode].urgentTaskAssignmentTooltip || "Assign urgent tasks to selected field agents"} tooltipPosition="top" wrapperClassName="w-full sm:w-auto" className="w-full sm:w-auto !h-10 !min-h-[40px] !rounded-[10px] !px-3 !py-2 !text-xs sm:!px-4 sm:!text-sm shrink-0" data-testid="urgent-assignment-button">
                                    <Zap size={18} className="text-white" fill="currentColor" /> {translations[langMode].urgentTaskAssignment || "Urgent Task Assignment"}
                                </CommonButton>
                            )}
                            <CommonButton colorIntent="primary" onClick={handleClick} variant="primary" tooltipText={translations[langMode].addnewspecialassingmentrule} tooltipPosition="top" disabled={!customVarPerm?.canCreate} wrapperClassName={`w-full sm:w-auto ${canUrgentAssign ? "" : "col-span-2"}`} className={`w-full sm:w-auto !h-10 !min-h-[40px] !rounded-[10px] !px-3 !py-2 !text-xs sm:!px-4 sm:!text-sm shrink-0 ${!customVarPerm?.canCreate ? 'cursor-not-allowed opacity-50' : ''}`}>
                                <Plus size={18} /> {translations[langMode].addNewRule}
                            </CommonButton>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="relative z-20 flex flex-wrap items-center justify-between gap-2 sm:gap-3 border-b border-slate-100 bg-white px-3 py-2.5 sm:px-5">
                    <p className="flex items-center gap-2 text-xs text-slate-500 sm:gap-4">
                        <span className="text-xs text-slate-500 whitespace-nowrap sm:hidden">
                            <span className="rtl-keep-ltr">{`${displayStart}-${displayEnd}`}</span>
                            {` / `}
                            <span className="rtl-keep-ltr">{totalItems ?? 0}</span>
                        </span>
                        <span className="hidden sm:inline whitespace-nowrap">
                            {`${translations[langMode].showing || "Showing"} `}
                            <span className="rtl-keep-ltr">{`${displayStart}-${displayEnd}`}</span>
                            {` ${translations[langMode].of || "of"} `}
                            <span className="rtl-keep-ltr">{totalItems ?? 0}</span>
                        </span>
                    </p>
                    <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:gap-3">
                        <div className="flex items-center gap-1.5">
                            <label htmlFor="pageSize" className="hidden sm:inline whitespace-nowrap">{translations[langMode].rows}</label>
                            <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} height="32px" fontSize="12px" />
                        </div>
                        <span className="pager-nav flex items-center gap-1">
                            <PagerArrowButton
                                direction="prev"
                                isRtl={isRtl}
                                onClick={goToPreviousPage}
                                disabled={currentPage === 0}
                                ariaLabel={translations[langMode]?.previousPage || "Previous page"}
                            />
                            <span className="text-xs text-slate-600 px-1 whitespace-nowrap">
                                {currentPage + 1} / {Math.max(totalPages, 1)}
                            </span>
                            <PagerArrowButton
                                direction="next"
                                isRtl={isRtl}
                                onClick={goToNextPage}
                                disabled={currentPage >= totalPages - 1}
                                ariaLabel={translations[langMode]?.nextPage || "Next page"}
                            />
                        </span>
                    </div>
                </div>

                {loading ? (
                    <div className="py-10">
                        <Spinner />
                    </div>
                ) : (
                    <TableScrollShell theme={theme} className="w-full flex-1 min-h-0" innerClassName="task-list-scrollbar">
                        <table className="w-full text-xs border-separate border-spacing-0" dir={isRtl ? "rtl" : "ltr"}>
                            <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
                                <tr>
                                    <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-center border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-400 w-14">
                                        {translations[langMode].number}
                                    </th>
                                    <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                                        {translations[langMode].assignTo || translations[langMode].FieldAgentName}
                                    </th>
                                    <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                                        {translations[langMode].sarWorkflow || "Workflow"}
                                    </th>
                                    <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                                        {translations[langMode].geoCodes}
                                    </th>
                                    <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                                        {translations[langMode].status}
                                    </th>
                                    {showAssignActionColumn && (
                                        <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-right border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                                            {translations[langMode].Action}
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {displayedSpecialAssign.length > 0 ? (
                                    displayedSpecialAssign.map((item, index) => {
                                        const scope = item.groupName ? "groups" : "users";
                                        const targetName = item.groupName || item.userName;
                                        return (
                                            <tr
                                                className="hover:bg-slate-50/70 transition-colors"
                                                key={item.id || index}
                                                onClick={() => setSelectedItem(item)}
                                            >
                                                <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100 text-center">
                                                    <span className="font-mono text-xs text-[#70788f] whitespace-nowrap">
                                                        {currentPage * pageSize + index + 1}
                                                    </span>
                                                </td>
                                                <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100">
                                                    <AssignToCell name={targetName} scopeLabel={scopeBadgeLabel(scope)} />
                                                </td>
                                                <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100">
                                                    <span
                                                        className={`text-xs truncate inline-block max-w-[180px] align-middle text-slate-700 font-medium`}
                                                        title={workflowLabel(item)}
                                                    >
                                                        {workflowLabel(item)}
                                                    </span>
                                                </td>
                                                <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100 whitespace-normal">
                                                    {renderChipCell(item, 'areaCodes', 'allAreaCodes', translations[langMode].geoCodes)}
                                                </td>
                                                <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100 whitespace-nowrap">
                                                    <span
                                                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md"
                                                        style={
                                                            item.active === true
                                                                ? { color: "#188A4B", background: "#E7F6ED" }
                                                                : { color: "#C22B3B", background: "#FBE9EB" }
                                                        }
                                                    >
                                                        <span
                                                            className="w-1.5 h-1.5 rounded-full shrink-0"
                                                            style={{ background: item.active === true ? "#188A4B" : "#C22B3B" }}
                                                        />
                                                        {item.active === true
                                                            ? translations[langMode].active || "Active"
                                                            : translations[langMode].inactive || "Inactive"}
                                                    </span>
                                                </td>
                                                {showAssignActionColumn && (
                                                    <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100">
                                                        <div className="flex items-center justify-end gap-1.5">
                                                            <ActionIconButton
                                                                tone="blue"
                                                                disabled={!customVarPerm?.canUpdate}
                                                                ariaLabel={translations[langMode].editRule || "Edit Rule"}
                                                                tooltipText={translations[langMode].editRule}
                                                                tooltipPosition="top"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setUpdateRule(item);
                                                                    setUpdateOpen(true);
                                                                }}
                                                                className="!h-7 !w-7 !rounded-md"
                                                            >
                                                                <Pencil size={14} />
                                                            </ActionIconButton>
                                                            <ActionIconButton
                                                                tone="red"
                                                                disabled={!customVarPerm?.canDelete}
                                                                ariaLabel={translations[langMode].deleteRule || "Delete Rule"}
                                                                tooltipText={translations[langMode].deleteRule}
                                                                tooltipPosition="top"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setDeleteRule(item);
                                                                }}
                                                                className="!h-7 !w-7 !rounded-md"
                                                            >
                                                                <Trash2 size={14} />
                                                            </ActionIconButton>
                                                            <ActionIconButton
                                                                tone="emerald"
                                                                disabled={!customVarPerm?.canCreate || !item.userId}
                                                                ariaLabel={translations[langMode].selectActivityAndMap}
                                                                tooltipText={translations[langMode].selectActivityAndMap}
                                                                tooltipPosition="top"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setUserId(item.userId);
                                                                    setOpenModal(true);
                                                                }}
                                                                className="!h-7 !w-7 !rounded-md"
                                                            >
                                                                <Map size={14} />
                                                            </ActionIconButton>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={showAssignActionColumn ? 6 : 5} className="py-16 h-[200px] align-middle">
                                            <div className="flex flex-col items-center justify-center text-center">
                                                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3 bg-[#E0EBF4] text-[#236DB4]">
                                                    {search.trim() ? <SearchX size={22} /> : <Inbox size={22} />}
                                                </div>
                                                <div className="text-sm font-semibold text-slate-800">
                                                    {search.trim() ? translations[langMode].noSearchResults : translations[langMode].nodatavailvle}
                                                </div>
                                                <div className="text-xs mt-1 max-w-xs text-slate-400">
                                                    {search.trim() ? translations[langMode].tryAdjustingSearch : translations[langMode].getStartedAddingRule}
                                                </div>
                                                {search.trim() ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setSearch("")}
                                                        className="mt-3 h-8 px-3 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
                                                    >
                                                        {translations[langMode].clearSearch}
                                                    </button>
                                                ) : (
                                                    customVarPerm?.canCreate && (
                                                        <button data-button-intent="primary"
                                                            type="button"
                                                            onClick={handleClick}
                                                            className="mt-3 h-8 px-3 rounded-lg text-xs font-medium text-white bg-[#236DB4] hover:bg-[#1d5a96]"
                                                        >
                                                            {translations[langMode].addNewRule}
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </TableScrollShell>
                )}
            </div>

            {selectedItem && (
                <ChangeRuleModal
                    open={open}
                    setOpen={setOpen}
                    selectedItem={selectedItem}
                    setSpecialAssign={setSpecialAssign}
                />
            )}
            <NewRuleModal
                newRule={newRule}
                setNewRule={setNewRule}
                workflowOptions={workflowOptions}
                defaultWorkflowId={workflowFilter?.value ?? null}
                onSaved={handleRulesSaved}
            />
            <UpdateRuleModal
                updateOpen={updateOpen}
                setUpdateOpen={setUpdateOpen}
                updateRule={updateRule}
                setUpdateRule={setUpdateRule}
                workflowOptions={workflowOptions}
                onSaved={handleRulesSaved}
            />
            {deleteRule !== null && <DeletePermission
                deleteFunction={handleDeleteSpecialAssignRule}
                onClose={() => setDeleteRule(null)}
                onDeleteText={translations[langMode].aresurewantodeleterule}
            />}

            <UrgentAssignmentModal
                open={urgentOpen && canUrgentAssign}
                setOpen={setUrgentOpen}
                workflowOptions={workflowOptions}
            />

            <ActivityMapFilter openModal={openModal} setOpenModal={setOpenModal} userId={userId} />
        </div>
    );
};

export default AssignmentTable;
